import { getConfig } from "../constants/config";
import { getTotalAmountOut } from "./getTotalAmountOut";
import { ethers } from "ethers-v5";

/**
 * Calculates the minimum output amount for a swap considering slippage tolerance.
 *
 * IMPORTANT: This function does NOT deduct fees from the minimum amount.
 * The Symphony smart contract validates minTotalAmountOut BEFORE deducting fees,
 * so we must calculate the minimum based on the pre-fee amount.
 *
 * @param {Object} params - The parameters object
 * @param {(Array|string|ethers.BigNumber|bigint)} params.variable - The input value. Can be:
 *   - Array: A route array from getRoute
 *   - string: A number in string format (raw or formatted)
 *   - ethers.BigNumber: Amount in BigNumber format
 *   - bigint: Amount in BigInt format
 * @param {(string|number)} [params.slippageAmount=0.5] - Slippage tolerance. Defaults to config value (0.5%)
 *   - If isBps=false: Percentage (e.g., "0.5" for 0.5%)
 *   - If isBps=true: Basis points (e.g., 10000 for 100%)
 * @param {Object} [params.options] - Calculation options
 * @param {boolean} [params.options.isRaw=true] - Controls input/output format:
 *   - true: Treats string inputs as raw units (wei), returns ethers.BigNumber
 *   - false: Treats string inputs as formatted (parseUnits), returns formatted string
 * @param {boolean} [params.options.isBps=false] - If true, slippage is in basis points
 * @param {number} [params.options.outTokenDecimals] - Decimals for output token
 *   Required when isRaw=false
 *
 * @returns {Promise<ethers.BigNumber|string>} The minimum output amount
 *   - ethers.BigNumber if isRaw=true (for contract calls)
 *   - formatted string if isRaw=false (for display/logging)
 *
 * @throws {Error} If variable or slippageAmount is missing/invalid
 * @throws {Error} If slippage calculation fails
 * @throws {Error} If slippageAmount is negative
 * @throws {Error} If outTokenDecimals is missing when isRaw=false
 *
 * @example
 * // Example 1: Calculate min amount out for a route
 * const route = await getRoute({ ... });
 * const minAmountOut = await calculateAmountOutMin({
 *   variable: route,
 *   slippageAmount: "0.5" // 0.5% slippage
 * });
 *
 * @example
 * // Example 2: Calculate min amount for raw amount
 * const minAmount = await calculateAmountOutMin({
 *   variable: "1000000000000000000", // 1 ETH in wei
 *   slippageAmount: "50",
 *   options: {
 *     isRaw: true,
 *     isBps: true // Using basis points
 *   }
 * });
 *
 * @example
 * // Example 3: Calculate min amount for formatted amount
 * const minAmount = await calculateAmountOutMin({
 *   variable: "1.0", // 1 ETH
 *   slippageAmount: "0.5",
 *   options: {
 *     isRaw: false,
 *     outTokenDecimals: 18
 *   }
 * });
 */
export const calculateAmountOutMin = async ({
  variable, // route or amount
  slippageAmount = getConfig().slippage,
  options = {},
}) => {
  // Merge with defaults to handle partial options (Bug #4 fix)
  const mergedOptions = {
    isRaw: true,
    isBps: false,
    outTokenDecimals: undefined,
    ...options,
  };

  // Bug #6 fix: Validate outTokenDecimals is provided when isRaw=false
  if (!mergedOptions.isRaw && mergedOptions.outTokenDecimals === undefined) {
    throw new Error(
      "Error: outTokenDecimals is required when isRaw=false. Please provide the token decimals."
    );
  }
  let amountOut = ethers.BigNumber.from(0);
  let slippage = 0;
  if (
    !variable ||
    !slippageAmount ||
    (Array.isArray(variable) && variable?.length === 0)
  ) {
    throw new Error("Invalid or missing `variable` or `slippageAmount` arg.");
  }

  //If variable is Route
  if (Array.isArray(variable) && variable.length !== 0)
    amountOut = (await getTotalAmountOut(variable)).amountOut;

  //If variable is raw string
  if (typeof variable === "string" && mergedOptions.isRaw)
    amountOut = ethers.BigNumber.from(variable);

  // If variable is formatted string
  if (typeof variable === "string" && !mergedOptions.isRaw)
    amountOut = ethers.utils.parseUnits(variable, mergedOptions.outTokenDecimals);

  // If variable is bigint
  if (typeof variable === "bigint")
    amountOut = ethers.BigNumber.from(variable.toString());
  if (ethers.BigNumber.isBigNumber(variable)) amountOut = variable;

  // Bug #5 fix: Validate slippage is not negative
  if (Number(slippageAmount) < 0) {
    throw new Error("Error setting slippage: Slippage amount cannot be negative.");
  }

  // if slippage is formatted string or float/number
  if (
    (typeof slippageAmount === "string" ||
      typeof slippageAmount === "number") &&
    !mergedOptions.isBps
  )
    slippage = (Number(slippageAmount) / 100).toString();
  // If slippage is bps units
  if (mergedOptions.isBps) slippage = (Number(slippageAmount) / 10000).toString();

  if (Number(slippageAmount) === 0) {
    console.error(
      "Slippage is set to 0. Please set slippage to a non-zero value if not intended."
    );
    // Bug #2 fix: Return in requested format
    if (mergedOptions.isRaw) return amountOut;
    return ethers.utils.formatUnits(amountOut, mergedOptions.outTokenDecimals);
  }

  if (
    (Number(slippageAmount) >= 100 && !mergedOptions.isBps) ||
    (Number(slippageAmount) >= 10000 && mergedOptions.isBps)
  ) {
    console.error(
      "Slippage is set to 100% or higher. Please set slippage to a lower value if not intended."
    );
    return ethers.BigNumber.from(0).toString();
  }

  if (!slippage || amountOut.isZero()) {
    if (!slippage) throw new Error("Error setting slippage!");
    if (amountOut.isZero())
      throw new Error(
        "Error setting slippage: No amount out provided! This might mean swap value is too low. (Return token amount is 0)"
      );
  }
  const integerPart = slippage.split(".")[0] || "0";
  const fractionalPart = slippage.split(".")[1] || "";

  const numerator = (
    integerPart.toString() + fractionalPart.toString()
  ).replace(/^0+|0+$/g, "");

  const denominatorExp = fractionalPart.length;

  const numeratorBN = ethers.BigNumber.from(numerator);
  const denominatorBN = ethers.BigNumber.from(10).pow(denominatorExp);

  let amountOutMin = amountOut.sub(
    amountOut.mul(numeratorBN).div(denominatorBN)
  );

  if (amountOutMin.isNegative()) {
    console.error(
      "Error setting slippage: Minimum amount is negative. This might mean slippage value is higher than 100%. Minimum amount set to `0`."
    );
    return ethers.BigNumber.from(0).toString();
  }

  // NOTE: We do NOT deduct fees here! (Bug #1 fix)
  //
  // IMPORTANT: The Symphony smart contract validates minTotalAmountOut BEFORE deducting fees.
  // Contract flow (Symphony.sol lines 554-566):
  //   1. Execute swaps → finalTokenAmount
  //   2. Check: finalTokenAmount >= minTotalAmountOut  // BEFORE fees!
  //   3. Process fees: amountToTransfer = finalTokenAmount - fees
  //   4. Transfer amountToTransfer to user
  //
  // If we deducted fees here, the validation would be too lenient:
  //   Example: DEXs return 94.5 tokens, slippage 5%, fee 1%
  //   BUGGY: minTotalAmountOut = 100 - 5% - 1% = 94.05
  //          Contract check: 94.5 >= 94.05? ✅ PASS (bad!)
  //   FIXED: minTotalAmountOut = 100 - 5% = 95
  //          Contract check: 94.5 >= 95? ❌ REVERT (correct!)
  //
  // Therefore, we return the pre-fee minimum amount:
  //   minTotalAmountOut = amountOut - slippage  (fees NOT deducted)

  // Bug #2 fix: Return in requested format
  if (mergedOptions.isRaw) return amountOutMin;
  return ethers.utils.formatUnits(amountOutMin, mergedOptions.outTokenDecimals);
};
